import { ApiError } from "./apiError";

export type ScanErrorKind =
  | "wrongDomain"
  | "rateLimited"
  | "network"
  | "server"
  | "unknown";

export interface ScanErrorInfo {
  kind: ScanErrorKind;
  /** What the user reads. */
  message: string;
  /** Short technical line the user can read out or screenshot when reporting. */
  detail: string;
}

function describeCause(e: unknown): string {
  if (e instanceof Error) {
    const name = e.name && e.name !== "Error" ? `${e.name}: ` : "";
    return `${name}${e.message || "(no message)"}`;
  }
  if (typeof e === "string" && e.length > 0) return e;
  return `unbekannter Fehler (${typeof e})`;
}

/**
 * Classify a failed scan into a screen decision, a message, and a detail.
 *
 * The detail exists because of a real incident: a scan failed twice for a
 * colleague and left nothing behind — no server log, and nothing on screen
 * beyond "Analyse fehlgeschlagen." — so there was no way to tell an aborted
 * upload from a rejected token. Every failure now carries something reportable.
 */
export function describeScanError(e: unknown): ScanErrorInfo {
  if (e instanceof ApiError) {
    const detail = `HTTP ${e.status} · ${e.message || "(no detail)"}`;
    if (e.status === 403) {
      return { kind: "wrongDomain", message: "Diese Domain ist nicht freigegeben.", detail };
    }
    // The browser never obtained an App Check token (see appCheck.ts). Almost
    // always a configuration problem rather than anything the user did, so say
    // that instead of blaming the scan.
    if (e.message === "appcheck_unavailable") {
      return {
        kind: "server",
        message: "Sicherheitsprüfung fehlgeschlagen. Bitte die App über die offizielle Adresse öffnen, sonst bei der IT melden.",
        detail,
      };
    }
    if (e.status === 429) {
      const minutes = Math.round((e.retryAfter ?? 3600) / 60);
      return {
        kind: "rateLimited",
        message: `Zu viele Scans. Bitte in ca. ${minutes} Minuten erneut versuchen.`,
        detail,
      };
    }
    return {
      kind: "server",
      message: "Analyse fehlgeschlagen. Bitte erneut versuchen.",
      detail,
    };
  }

  // fetch() rejects with a TypeError when the request never completed —
  // offline, DNS failure, or the tab losing the network mid-upload.
  if (e instanceof TypeError) {
    return {
      kind: "network",
      message: "Keine Verbindung zum Server. Bitte Internet prüfen und erneut versuchen.",
      detail: describeCause(e),
    };
  }

  return {
    kind: "unknown",
    message: "Analyse fehlgeschlagen. Bitte erneut versuchen.",
    detail: describeCause(e),
  };
}
